// src/hooks/useLazyBackgroundImages.js
import { useEffect, useRef } from "react";

/**
 * Globally lazy-loads / unloads background-images for every element that has
 *  data-lazy-bg="true"  and  data-src="<real-url>"
 *
 *  – when the element enters the viewport (+ rootMargin)       → load
 *  – when it leaves                                           → unload
 *  – if current scroll-speed is above velocityThreshold (px/s) → postpone load
 */
export default function useLazyBackgroundImages({
  rootMargin = "300px",        // pre-load while still off-screen
  threshold = 0.01,
  velocityThreshold = 2500     // px / second
} = {}) {
  const speedRef   = useRef(0);
  const lastScroll = useRef({ y: 0, t: Date.now() });
  const pendingRef = useRef(new Set()); // Elements pending load when fast scroll

  /* -------------------------------------------------------------------- */
  /* 1. Scroll-speed tracker                                              */
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    const onScroll = () => {
      const now = Date.now();
      const dy = Math.abs(window.scrollY - lastScroll.current.y);
      const dt = now - lastScroll.current.t;
      if (dt > 40) {
        speedRef.current = (dy / dt) * 1000;
        lastScroll.current = { y: window.scrollY, t: now };
      }
      // Always flush pending loads when below threshold
      if (speedRef.current < velocityThreshold && pendingRef.current.size > 0) {
        pendingRef.current.forEach(el => {
          const src = el.dataset.src;
          if (src) el.style.backgroundImage = `url(${src})`;
        });
        pendingRef.current.clear();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [velocityThreshold]);

  /* -------------------------------------------------------------------- */
  /* 2. IntersectionObserver + MutationObserver                           */
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Extract loading logic into a separate function
    const load = el => {
      const src = el.dataset.src;
      if (!src) return;
      if (speedRef.current < velocityThreshold) {
        el.style.backgroundImage = `url(${src})`;
      } else {
        // Queue for later when scroll slows
        pendingRef.current.add(el);
      }
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          if (entry.isIntersecting) {
            load(el);
          } else {
            // Free memory as soon as the element is gone
            el.style.backgroundImage = "none";
            // Remove from pending if unloaded
            pendingRef.current.delete(el);
          }
        });
      },
      { rootMargin, threshold }
    );

    // Attach observer to every existing candidate
    const attachAll = () =>
      document
        .querySelectorAll("[data-lazy-bg]")
        .forEach((el) => io.observe(el));
    attachAll();

    // Watch the DOM so lazily produced elements are also observed
    const mo = new MutationObserver((muts) => {
      muts.forEach((m) => {
        // Handle attribute changes
        if (m.type === 'attributes' && m.attributeName === 'data-src') {
          load(m.target); // reload immediately if in view
        }
        // Handle added nodes
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches?.("[data-lazy-bg]")) io.observe(n);
          n.querySelectorAll?.("[data-lazy-bg]").forEach((c) => io.observe(c));
        });
      });
    });
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-src'] // listen to src changes
    });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [rootMargin, threshold, velocityThreshold]);
}
